/**
 * Court-day arithmetic.
 *
 * calendarFor(court) gives a court's calendar: weekends, the statewide judicial holidays
 * (CCP 135, which takes its days from GC 6700 and 6701), and the court's own published
 * holiday schedule and closures from its entry in src/courts/, where it has one.
 *
 * The top-level HOLIDAYS, isCourtDay, addCourtDays and prevCourtDay are San Francisco's
 * calendar, kept for the motion-to-quash wizard until it moves onto calendarFor (issue #37 follow-up).
 */
import SF from "../courts/sf-superior.js";

const pad = (n) => String(n).padStart(2, "0");
export const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parse = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const todayISO = () => iso(new Date());
export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
export const fmt = (d) =>
  d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
export const fmtISO = (s) => (s ? fmt(parse(s)) : "");

/* ------------------------------------------------------- statewide holidays */
// nth (1-based) weekday of a month; n = -1 for the last one. weekday: 0 = Sunday.
function nthWeekday(year, month, weekday, n) {
  if (n < 0) {
    const d = new Date(year, month + 1, 0);
    while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
    return d;
  }
  const d = new Date(year, month, 1);
  while (d.getDay() !== weekday) d.setDate(d.getDate() + 1);
  d.setDate(d.getDate() + 7 * (n - 1));
  return d;
}

// Fixed dates that GC 6701(a) moves to the following Monday when they fall on a Sunday.
// (September 9 is also in 6701(a), but CCP 135 excludes it as a judicial holiday.)
const SUNDAY_TO_MONDAY = new Set(["01-01", "02-12", "03-31", "07-04", "11-11", "12-25"]);

/**
 * The judicial holidays CCP 135 sets for one year. Each entry is { date, name, weekend }.
 * A holiday on a weekend moves to a weekday only where GC 6701 says so: a Sunday date listed
 * in 6701(a) to the Monday after, and November 11 on a Saturday to the Friday before (6701(b)).
 * Any other holiday on a weekend keeps its date with weekend "saturday" or "sunday": for a
 * Saturday, CCP 135 lets the Judicial Council designate another day, and which day it picks
 * is not in data/, so no weekday is added for it here.
 * Good Friday (noon to 3 p.m.) is left out: CCP 135 counts only full days.
 */
export function statewideHolidays(year) {
  const out = [];
  const add = (d, name) => {
    const w = d.getDay();
    const md = iso(d).slice(5);
    if (w === 0 && SUNDAY_TO_MONDAY.has(md)) out.push({ date: iso(addDays(d, 1)), name: `${name} (observed)`, weekend: "" });
    else if (w === 6 && md === "11-11") out.push({ date: iso(addDays(d, -1)), name: `${name} (observed)`, weekend: "" });
    else out.push({ date: iso(d), name, weekend: w === 6 ? "saturday" : w === 0 ? "sunday" : "" });
  };
  add(new Date(year, 0, 1), "New Year's Day");
  add(nthWeekday(year, 0, 1, 3), "Dr. Martin Luther King, Jr. Day");
  add(new Date(year, 1, 12), "Lincoln Day");
  add(nthWeekday(year, 1, 1, 3), "Third Monday in February");
  add(new Date(year, 2, 31), "Farmworkers Day");
  add(nthWeekday(year, 4, 1, -1), "Last Monday in May");
  add(new Date(year, 5, 19), "Juneteenth");
  add(new Date(year, 6, 4), "July 4");
  add(nthWeekday(year, 8, 1, 1), "First Monday in September");
  add(nthWeekday(year, 8, 5, 4), "Native American Day");
  add(new Date(year, 10, 11), "Veterans Day");
  const thanksgiving = nthWeekday(year, 10, 4, 4);
  add(thanksgiving, "Thanksgiving Day");
  add(addDays(thanksgiving, 1), "Day after Thanksgiving");
  add(new Date(year, 11, 25), "Christmas Day");
  return out;
}

/* ------------------------------------------------------------ court calendar */
/**
 * A court's calendar. `court` is a court context (lib/courtlayer.js) or any object with
 * `name` and, optionally, `holidays`: { observed: [iso], closures: [{ date, name }], through, cite }.
 *
 * - observed: the court's published holiday schedule, which also shows the weekday it
 *   observes for a holiday that falls on a weekend. Used through `through`.
 * - closures: other days the court is closed.
 *
 * notes(from, to) lists what the dates between two days rest on that data/ doesn't settle,
 * for the checklist's "verify" section.
 */
export function calendarFor(court) {
  const name = (court && court.name) || "the court";
  const own = (court && court.holidays) || {};
  const observed = new Set(own.observed || []);
  const closures = new Map((own.closures || []).map((c) => [c.date, c.name]));
  const through = own.through || "";
  const byYear = new Map();
  const statewide = (year) => {
    if (!byYear.has(year)) byYear.set(year, statewideHolidays(year));
    return byYear.get(year);
  };
  const isHoliday = (d) => {
    const s = iso(d);
    if (observed.has(s) || closures.has(s)) return true;
    return statewide(d.getFullYear()).some((h) => h.date === s && !h.weekend);
  };
  const isCourtDay = (d) => {
    const w = d.getDay();
    return w !== 0 && w !== 6 && !isHoliday(d);
  };
  function addCourtDaysFor(d, n) {
    let x = new Date(d);
    const step = n < 0 ? -1 : 1;
    let left = Math.abs(n);
    while (left > 0) {
      x = addDays(x, step);
      if (isCourtDay(x)) left--;
    }
    return x;
  }
  // The day itself if it is a court day, else the next court day (CCP 12a moves a last day that is a holiday).
  const onOrNextCourtDay = (d) => (isCourtDay(d) ? new Date(d) : addCourtDaysFor(d, 1));

  function notes(from, to) {
    const a = from < to ? from : to;
    const b = from < to ? to : from;
    const out = [];
    const coveredByList = (s) => through && s <= through;
    if (!through) {
      out.push(`Court holidays for ${name} are worked out from CCP 135 and GC 6700-6701; the court's own holiday schedule is not in the downloaded files. Check it before relying on the dates.`);
    } else if (iso(b) > through) {
      out.push(`The dates run past the holiday schedule of ${name} in the downloaded files (through ${fmtISO(through)}). Later dates use the statewide judicial holidays only. Check the court's schedule.`);
    }
    // A weekend holiday matters when it, or the weekday next to it that might be observed instead, is in range.
    const lo = iso(addDays(a, -1));
    const hi = iso(addDays(b, 1));
    for (let y = a.getFullYear(); y <= b.getFullYear() + 1; y++) {
      statewide(y).filter((h) => h.weekend && h.date >= lo && h.date <= hi && !coveredByList(h.date)).forEach((h) => {
        out.push(h.weekend === "saturday"
          ? `${h.name} falls on a Saturday (${fmtISO(h.date)}). The Judicial Council may designate another day to observe it (CCP 135). Check which day ${name} is closed.`
          : `${h.name} falls on a Sunday (${fmtISO(h.date)}) and GC 6701(a) does not move it. Check whether ${name} closes on another day for it.`);
      });
    }
    return out;
  }

  return {
    name,
    through,
    isHoliday,
    isCourtDay,
    addCourtDays: addCourtDaysFor,
    prevCourtDay: (d) => addCourtDaysFor(d, -1),
    nextCourtDay: (d) => addCourtDaysFor(d, 1),
    onOrNextCourtDay,
    notes,
    cite: own.cite ? `CCP 135; GC 6700-6701; ${own.cite}` : "CCP 135; GC 6700-6701",
  };
}

/* --------------------------------------- San Francisco (motion-to-quash wizard) */
export const HOLIDAYS = new Set(SF.holidays.observed);
export const HOLIDAY_DATA_THROUGH = SF.holidays.through;
export const isCourtDay = (d) => {
  const w = d.getDay();
  return w !== 0 && w !== 6 && !HOLIDAYS.has(iso(d));
};
export function addCourtDays(d, n) {
  let x = new Date(d);
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    x = addDays(x, step);
    if (isCourtDay(x)) left--;
  }
  return x;
}
export const prevCourtDay = (d) => addCourtDays(d, -1);
